import { Tables } from "../../../database.types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Chip,
  ImageList,
  ImageListItem,
  Paper,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Save,
  CloudUpload,
  Delete,
  CategoryOutlined,
  QuizOutlined,
  CheckCircleOutline,
  HelpOutline,
} from "@mui/icons-material";
import useRoundAttachments from "../useRoundAttachments";
import { StandardButton } from "../../components/StandardButton";
import { StandardTextInput } from "../../components/StandardTextInput";
import {
  AutoSaveIndicator,
  AutoSaveStatus,
} from "../../components/AutoSaveIndicator";
import { useSupabase } from "../../auth/useSupabase";
import { gameService } from "../../data/gameService";
import { debounce } from "lodash";

interface RoundEditorProps {
  roundId: string;
  gameId: string;
  roundNumber: number;
  isNiche: boolean;
  disabled: boolean;
  refreshTabState: () => void;
}

type QuestionRow = Tables<"questions">;

const DEBOUNCE_MS = 300;

const RoundEditor = (props: RoundEditorProps) => {
  const { roundId, gameId, roundNumber, isNiche, disabled, refreshTabState } =
    props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const { supabase } = useSupabase();

  const [loading, setLoading] = useState(true);

  // Form state
  const [category, setCategory] = useState("");
  const [question1, setQuestion1] = useState<QuestionRow | null>(null);
  const [question2, setQuestion2] = useState<QuestionRow | null>(null);
  const [team1NicheCategory, setTeam1NicheCategory] = useState("");
  const [team2NicheCategory, setTeam2NicheCategory] = useState("");

  // Refs for niche values so debounced save always uses latest
  const team1NicheRef = useRef("");
  const team2NicheRef = useRef("");

  // Auto-save indicator
  const [autoSaveState, setAutoSaveState] = useState<AutoSaveStatus>("idle");
  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (autoSaveState === "saved") {
      refreshTabState();
    }
  }, [autoSaveState]);

  const markSaving = useCallback(() => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    setAutoSaveState("saving");
  }, []);

  const markSaved = useCallback(() => {
    setAutoSaveState("saved");
    savedTimerRef.current = setTimeout(() => setAutoSaveState("idle"), 2000);
  }, []);

  const markError = useCallback(() => {
    setAutoSaveState("error");
    savedTimerRef.current = setTimeout(() => setAutoSaveState("idle"), 3000);
  }, []);

  // ── Fetch round + questions on mount ──────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [round, questions] = await Promise.all([
          gameService.getRoundById(supabase, roundId),
          gameService.getRoundQuestions(supabase, roundId),
        ]);
        if (cancelled) return;

        if (round) setCategory(round.category);

        setQuestion1(questions.find((q) => q.question_number === 1) ?? null);
        setQuestion2(questions.find((q) => q.question_number === 2) ?? null);

        if (isNiche) {
          const game = await gameService.getById(supabase, gameId);
          if (!cancelled && game) {
            const t1 = game.team_1_niche_category ?? "";
            const t2 = game.team_2_niche_category ?? "";
            setTeam1NicheCategory(t1);
            setTeam2NicheCategory(t2);
            team1NicheRef.current = t1;
            team2NicheRef.current = t2;
          }
        }
      } catch (err) {
        console.error("Error loading round data:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [supabase, roundId, gameId, isNiche]);

  // ── Debounced save functions ──────────────────────────────────────────

  const debouncedSaveCategory = useMemo(
    () =>
      debounce(async (value: string) => {
        markSaving();
        try {
          await gameService.setRoundCategory(supabase, roundId, value);
          markSaved();
        } catch {
          markError();
        }
      }, DEBOUNCE_MS),
    [supabase, roundId, markSaving, markSaved, markError],
  );

  // Accumulate pending question updates so rapid changes to different fields all save
  const pendingQuestionUpdates = useRef<Map<string, Record<string, string>>>(
    new Map(),
  );

  const debouncedSaveQuestion = useMemo(
    () =>
      debounce(async () => {
        const pending = new Map(pendingQuestionUpdates.current);
        pendingQuestionUpdates.current.clear();
        if (pending.size === 0) return;

        markSaving();
        try {
          await Promise.all(
            Array.from(pending.entries()).map(([questionId, updates]) =>
              gameService.updateQuestion(supabase, questionId, updates),
            ),
          );
          markSaved();
        } catch {
          markError();
        }
      }, DEBOUNCE_MS),
    [supabase, markSaving, markSaved, markError],
  );

  const debouncedSaveNiche = useMemo(
    () =>
      debounce(async (t1: string, t2: string) => {
        markSaving();
        try {
          await gameService.setNicheCategories(
            supabase,
            gameId,
            t1 || null,
            t2 || null,
          );
          markSaved();
        } catch {
          markError();
        }
      }, DEBOUNCE_MS),
    [supabase, gameId, markSaving, markSaved, markError],
  );

  // Flush pending saves on unmount
  useEffect(() => {
    return () => {
      debouncedSaveCategory.flush();
      debouncedSaveQuestion.flush();
      debouncedSaveNiche.flush();
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, [debouncedSaveCategory, debouncedSaveQuestion, debouncedSaveNiche]);

  // ── Attachments ───────────────────────────────────────────────────────

  const {
    handleDeleteAttachment,
    loadAttachmentUrls,
    attachments1,
    attachments2,
    handleFileUpload,
    isUploading1,
    isUploading2,
  } = useRoundAttachments({
    question1Id: question1?.id ?? null,
    question2Id: question2?.id ?? null,
  });

  useEffect(() => {
    if (question1 || question2) {
      loadAttachmentUrls();
    }
  }, [question1?.id, question2?.id, loadAttachmentUrls]);

  // ── Change handlers ───────────────────────────────────────────────────

  const handleCategoryChange = (value: string) => {
    setCategory(value);
    debouncedSaveCategory(value);
  };

  const handleQuestionChange = (
    questionNum: 1 | 2,
    field: "question_text" | "answer_text",
    value: string,
  ) => {
    const setter = questionNum === 1 ? setQuestion1 : setQuestion2;
    setter((prev) => (prev ? { ...prev, [field]: value } : prev));

    const questionId = questionNum === 1 ? question1?.id : question2?.id;
    if (questionId) {
      const existing = pendingQuestionUpdates.current.get(questionId) ?? {};
      pendingQuestionUpdates.current.set(questionId, {
        ...existing,
        [field]: value,
      });
      debouncedSaveQuestion();
    }
  };

  const handleTeam1NicheChange = (value: string) => {
    setTeam1NicheCategory(value);
    team1NicheRef.current = value;
    debouncedSaveNiche(value, team2NicheRef.current);
  };

  const handleTeam2NicheChange = (value: string) => {
    setTeam2NicheCategory(value);
    team2NicheRef.current = value;
    debouncedSaveNiche(team1NicheRef.current, value);
  };

  const handleManualSave = () => {
    debouncedSaveCategory.flush();
    debouncedSaveQuestion.flush();
    debouncedSaveNiche.flush();
  };

  // ── Render ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box
        py={1}
        display="flex"
        justifyContent="center"
        alignItems="center"
        flex={1}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box py={1} overflow="auto" display="flex" flex={1} width="100%">
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: 720,
          mx: "auto",
          borderRadius: 2,
          boxShadow: 2,
          transition: "box-shadow 0.3s ease",
          display: "flex",
          flex: 1,
          flexDirection: "column",
          backgroundColor: theme.palette.primary.dark,
          "&:hover": {
            boxShadow: 4,
          },
          border: `1px solid ${theme.palette.primary.main}`,
        }}
      >
        <CardHeader
          title={
            <Box
              display="flex"
              width="100%"
              justifyContent="space-between"
              px={2}
              bgcolor={theme.palette.primary.dark}
            >
              <Typography
                variant="h5"
                component="h2"
                fontWeight="bold"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: { xs: "1.2rem", sm: "1.5rem" },
                  color: "white",
                }}
              >
                {isNiche ? <CategoryOutlined /> : <QuizOutlined />}
                {isNiche
                  ? `Niche Category Round (${category || "Unnamed"})`
                  : `Round ${roundNumber}: ${category || "Unnamed"}`}
              </Typography>

              <Box display="flex" mr="24px" flexShrink={0}>
                <AutoSaveIndicator status={autoSaveState} />
              </Box>
            </Box>
          }
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.primary.dark,
            p: { xs: 1, sm: 2 },
          }}
        />
        <CardContent
          sx={{
            p: { xs: 2, sm: 3 },
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            gap: 3,
          }}
        >
          {isNiche ? (
            <Box display="flex" gap={2}>
              <StandardTextInput
                label="Team 1 Niche Category"
                onChange={(e) => handleTeam1NicheChange(e.target.value)}
                value={team1NicheCategory}
                fullWidth
                disabled={disabled}
                inputProps={{ "data-testid": "team-1-niche-category-input" }}
                InputProps={{
                  startAdornment: (
                    <CategoryOutlined color="primary" sx={{ mr: 1 }} />
                  ),
                }}
              />
              <StandardTextInput
                label="Team 2 Niche Category"
                onChange={(e) => handleTeam2NicheChange(e.target.value)}
                value={team2NicheCategory}
                fullWidth
                disabled={disabled}
                inputProps={{ "data-testid": "team-2-niche-category-input" }}
                InputProps={{
                  startAdornment: (
                    <CategoryOutlined color="primary" sx={{ mr: 1 }} />
                  ),
                }}
              />
            </Box>
          ) : (
            <StandardTextInput
              label="Category"
              value={category}
              onChange={(e) => handleCategoryChange(e.target.value)}
              fullWidth
              disabled={disabled}
              required
              inputProps={{
                "data-testid": `round-${roundNumber}-category-input`,
              }}
              InputProps={{
                startAdornment: (
                  <CategoryOutlined color="primary" sx={{ mr: 1 }} />
                ),
              }}
            />
          )}

          {/* Question 1 */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              backgroundColor: "rgba(255, 255, 255, 0.03)",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "rgba(255, 255, 255, 0.7)",
                fontWeight: 600,
              }}
            >
              <HelpOutline fontSize="small" />
              Question 1
            </Typography>

            <StandardTextInput
              label="Question"
              value={question1?.question_text ?? ""}
              onChange={(e) =>
                handleQuestionChange(1, "question_text", e.target.value)
              }
              fullWidth
              disabled={disabled}
              inputProps={{
                "data-testid": `round-${roundNumber}-q1-question-input`,
              }}
            />

            <StandardTextInput
              label="Answer"
              value={question1?.answer_text ?? ""}
              onChange={(e) =>
                handleQuestionChange(1, "answer_text", e.target.value)
              }
              fullWidth
              disabled={disabled}
              inputProps={{
                "data-testid": `round-${roundNumber}-q1-answer-input`,
              }}
              InputProps={{
                startAdornment: (
                  <CheckCircleOutline color="success" sx={{ mr: 1 }} />
                ),
              }}
            />

            {/* Question 1 Attachments */}
            <Stack gap={1.5}>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {attachments1.map((attachment) => (
                  <Chip
                    key={attachment.id}
                    label={attachment.filename}
                    onDelete={() => handleDeleteAttachment(attachment.id, 1)}
                    deleteIcon={<Delete />}
                    variant="outlined"
                    color="primary"
                    disabled={disabled}
                  />
                ))}
              </Stack>

              {attachments1.length > 0 && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    borderColor: "rgba(255, 255, 255, 0.12)",
                    backgroundColor: "transparent",
                  }}
                >
                  <ImageList
                    sx={{
                      width: "100%",
                      height: "fit-content",
                      maxHeight: 300,
                      overflowY: "auto",
                    }}
                    cols={isMobile ? 2 : isTablet ? 3 : 4}
                    rowHeight={164}
                    gap={8}
                  >
                    {attachments1.map((attachment) => (
                      <ImageListItem key={attachment.id}>
                        <img
                          src={attachment.url}
                          alt={attachment.filename}
                          style={{
                            objectFit: "contain",
                            height: "100%",
                            width: "100%",
                            borderRadius: 4,
                          }}
                          loading="lazy"
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Paper>
              )}

              <StandardButton
                component="label"
                variant="outlined"
                color="primary"
                startIcon={
                  isUploading1 ? (
                    <CircularProgress size={20} />
                  ) : (
                    <CloudUpload />
                  )
                }
                disabled={isUploading1 || disabled}
                fullWidth={isMobile}
              >
                {isUploading1 ? "Uploading..." : "Upload Image"}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  multiple
                  onChange={(e) => handleFileUpload(e, 1)}
                />
              </StandardButton>
            </Stack>
          </Paper>

          {/* Question 2 */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              backgroundColor: "rgba(255, 255, 255, 0.03)",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "rgba(255, 255, 255, 0.7)",
                fontWeight: 600,
              }}
            >
              <HelpOutline fontSize="small" />
              Question 2
            </Typography>

            <StandardTextInput
              label="Question"
              value={question2?.question_text ?? ""}
              onChange={(e) =>
                handleQuestionChange(2, "question_text", e.target.value)
              }
              fullWidth
              disabled={disabled}
              inputProps={{
                "data-testid": `round-${roundNumber}-q2-question-input`,
              }}
            />

            <StandardTextInput
              label="Answer"
              value={question2?.answer_text ?? ""}
              disabled={disabled}
              onChange={(e) =>
                handleQuestionChange(2, "answer_text", e.target.value)
              }
              fullWidth
              onEnter={handleManualSave}
              inputProps={{
                "data-testid": `round-${roundNumber}-q2-answer-input`,
              }}
              InputProps={{
                startAdornment: (
                  <CheckCircleOutline color="success" sx={{ mr: 1 }} />
                ),
              }}
            />

            {/* Question 2 Attachments */}
            <Stack gap={1.5}>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {attachments2.map((attachment) => (
                  <Chip
                    key={attachment.id}
                    label={attachment.filename}
                    onDelete={() => handleDeleteAttachment(attachment.id, 2)}
                    deleteIcon={<Delete />}
                    variant="outlined"
                    color="primary"
                    disabled={disabled}
                  />
                ))}
              </Stack>

              {attachments2.length > 0 && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    borderColor: "rgba(255, 255, 255, 0.12)",
                    backgroundColor: "transparent",
                  }}
                >
                  <ImageList
                    sx={{
                      width: "100%",
                      height: "fit-content",
                      maxHeight: 300,
                      overflowY: "auto",
                    }}
                    cols={isMobile ? 2 : isTablet ? 3 : 4}
                    rowHeight={164}
                    gap={8}
                  >
                    {attachments2.map((attachment) => (
                      <ImageListItem key={attachment.id}>
                        <img
                          src={attachment.url}
                          alt={attachment.filename}
                          style={{
                            objectFit: "contain",
                            height: "100%",
                            width: "100%",
                            borderRadius: 4,
                          }}
                          loading="lazy"
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Paper>
              )}

              <StandardButton
                component="label"
                variant="outlined"
                color="primary"
                startIcon={
                  isUploading2 ? (
                    <CircularProgress size={20} />
                  ) : (
                    <CloudUpload />
                  )
                }
                disabled={isUploading2 || disabled}
                fullWidth={isMobile}
              >
                {isUploading2 ? "Uploading..." : "Upload Image"}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  multiple
                  onChange={(e) => handleFileUpload(e, 2)}
                />
              </StandardButton>
            </Stack>
          </Paper>

          {/* Save Button - For Mobile Users */}
          {isMobile && (
            <StandardButton
              variant="contained"
              color="primary"
              fullWidth
              disabled={disabled}
              startIcon={<Save />}
              onClick={handleManualSave}
              sx={{ mt: 3 }}
            >
              Save Round
            </StandardButton>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default RoundEditor;
